// Alt text for a photo, in one place. Order: the owner's description (page.imageAlts, keyed by the photo's id: bristol adds
// it, optional), the stock photo's own description (Pexels `alt`, which the server puts in the same map keyed by its address),
// then the fallbacks the caller gives (the section's heading or caption). Pure: no Vue, no server imports.
import {mediaIdsIn} from "./mediaUrl.js";

const ATTACHMENT_ID = /\/api\/models\/attachment\/([0-9a-fA-F-]{36})/;
const isObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const text = (v) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "");

// The photo id in a /media address (sized or not) or a signed attachment address, else "".
export function photoIdOf(src) {
    if (typeof src !== "string") return "";
    const media = mediaIdsIn(src)[0];
    if (media) return media;
    const m = src.match(ATTACHMENT_ID);
    return m ? m[1].toLowerCase() : "";
}

// Every photo id a piece of text points at, through /media or a bristol attachment address (the studio preview's).
export const photoIdsIn = (text) => [...new Set([...String(text ?? '').matchAll(/(?:\/media|\/api\/models\/attachment)\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/g)].map((m) => m[1].toLowerCase()))];

// The first non-empty of alts[id], alts[address], then the fallbacks. `alts` may be missing or not an object.
export function altFor(alts, src, ...fallbacks) {
    if (isObject(alts)) {
        const id = photoIdOf(src);
        const found = text(alts[id]) || text(alts[src]);
        if (found) return found;
    }
    for (const f of fallbacks) {
        const t = text(f);
        if (t) return t;
    }
    return "";
}

// Which of the owner's descriptions (uuid -> text, from public_attachment_alts) and Pexels descriptions this page uses: only the
// ids and stock photos the page holds, so the page data stays small. `data` is the finished page (sections and the rest).
export function pageImageAlts(data, ownerAlts) {
    const out = {};
    const json = JSON.stringify(data ?? {});
    if (isObject(ownerAlts)) {
        const byId = Object.fromEntries(Object.entries(ownerAlts).map(([k, v]) => [k.toLowerCase(), v]));
        for (const id of photoIdsIn(json)) {
            const t = text(byId[id]);
            if (t) out[id] = t;
        }
    }
    const walk = (v) => {
        if (Array.isArray(v)) return v.forEach(walk);
        if (!isObject(v)) return;
        const px = v.pexels;
        if (isObject(px) && isObject(px.src) && text(px.src.large) && text(px.alt)) out[px.src.large] = text(px.alt);
        Object.values(v).forEach(walk);
    };
    walk(data);
    return out;
}
